import { X } from "lucide-react";
import { type JSX, type ReactNode, useEffect, useRef, useState } from "react";
import {
  CBOM_CERTIFICATE_IDENTITY_COPY,
  CBOM_KEY_STORAGE_COPY,
  cbomProtocolRoleCopy,
  SBOM_UPGRADE_OPTION_COPY,
  sbomDependencyPathCopy,
} from "./inventory-copy.js";
import type {
  InventoryCbomRow,
  InventorySbomRow,
  InventorySelection,
} from "./inventory-selection.js";
import { displayRuleId } from "../../app/rule-id.js";
import "./inventory-briefing.css";

export type InventoryDetailTab = "summary" | "evidence" | "investigate" | "review";

const INVENTORY_TABS: readonly { id: InventoryDetailTab; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "evidence", label: "Evidence" },
  { id: "investigate", label: "Investigate" },
  { id: "review", label: "Review" },
];

function InventoryTabs({
  activeTab,
  onChange,
}: {
  activeTab: InventoryDetailTab;
  onChange: (tab: InventoryDetailTab) => void;
}) {
  const move = (tab: InventoryDetailTab, offset: number) => {
    const index = INVENTORY_TABS.findIndex((item) => item.id === tab);
    const next = INVENTORY_TABS[(index + offset + INVENTORY_TABS.length) % INVENTORY_TABS.length];
    if (!next) return;
    onChange(next.id);
    requestAnimationFrame(() => document.getElementById(`inventory-tab-${next.id}`)?.focus());
  };
  return (
    <div className="inventory-briefing__tabs" role="tablist" aria-label="Issue details">
      {INVENTORY_TABS.map((tab) => (
        <button
          type="button"
          role="tab"
          id={`inventory-tab-${tab.id}`}
          aria-controls={`inventory-panel-${tab.id}`}
          aria-selected={activeTab === tab.id}
          tabIndex={activeTab === tab.id ? 0 : -1}
          key={tab.id}
          onClick={() => onChange(tab.id)}
          onKeyDown={(event) => {
            if (event.key === "ArrowRight") {
              event.preventDefault();
              move(tab.id, 1);
            } else if (event.key === "ArrowLeft") {
              event.preventDefault();
              move(tab.id, -1);
            }
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function InventoryPanel({
  tab,
  activeTab,
  children,
}: {
  tab: InventoryDetailTab;
  activeTab: InventoryDetailTab;
  children: ReactNode;
}) {
  return (
    <section
      className="inventory-briefing__panel"
      role="tabpanel"
      id={`inventory-panel-${tab}`}
      aria-labelledby={`inventory-tab-${tab}`}
      hidden={activeTab !== tab}
    >
      {children}
    </section>
  );
}

function base64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function fileName(path: string): string {
  return path.split("/").at(-1) ?? path;
}

export function componentFindingsHref(component: InventorySbomRow): string {
  const componentKey = base64Url(
    JSON.stringify({
      ecosystem: component.ecosystem,
      name: component.name,
      version: component.version || null,
    }),
  );
  const params = new URLSearchParams({
    statuses: "",
    preset: "all",
    analysisFamilies: "sca,container",
    componentKeys: componentKey,
    scanId: component.scanId,
  });
  return `/findings?${params.toString()}`;
}

export function cryptoFindingsHref(row: InventoryCbomRow): string | null {
  if (!row.findingRuleId) return null;
  const params = new URLSearchParams({
    statuses: "",
    preset: "all",
    analysisFamilies: "pqc",
    ruleIds: row.findingRuleId,
    scanId: row.scanId,
  });
  return `/findings?${params.toString()}`;
}

export function cryptoDisplayName(row: InventoryCbomRow): string {
  const use = row.useContext.replaceAll("_", " ");
  if (row.algorithm?.toLocaleLowerCase() === "ec") {
    return `Elliptic-curve (EC) ${use}`;
  }
  if (
    row.algorithm &&
    /^\d{3,5}$/u.test(row.algorithm) &&
    /key establishment|key generation/u.test(use)
  ) {
    return `RSA-${row.algorithm} ${use}`;
  }
  if (row.algorithm) return `${row.algorithm} ${use}`;
  return `Dynamic algorithm ${use}`;
}

function sbomStamp(row: InventorySbomRow): string {
  if (!row.resolved) return "Unresolved";
  return row.local ? "Local" : "Third-party";
}

function sbomDependencyRole(row: InventorySbomRow): string {
  if (row.local) return "Repository-owned component";
  if (row.dependencyPath.length > 1) return "Transitive third-party dependency";
  return "Direct third-party dependency";
}

function sbomOwnerBoundary(row: InventorySbomRow): string {
  const directory = row.manifestPath.split("/").slice(0, -1).join("/");
  return directory || "Repository root";
}

function sbomSurgicalAction(row: InventorySbomRow): string {
  if (!row.resolved) {
    return `Resolve ${row.name} to an exact version in ${row.manifestPath}, regenerate the lockfile, then rerun SCA.`;
  }
  if (row.local) {
    return `Review the repository-owned ${row.name} component at ${row.manifestPath}; package-manager upgrade advice does not apply.`;
  }
  const parent = row.dependencyPath.length > 1 ? row.dependencyPath.at(-2) : null;
  if (parent) {
    return `Upgrade or replace parent ${parent}, regenerate ${row.manifestPath}, and verify that ${row.purl} disappears from the new lockfile.`;
  }
  return `Upgrade or remove the direct ${row.name} declaration, regenerate ${row.manifestPath}, and verify the replacement with a new receipted scan.`;
}

function SbomDependencyRoute({ row }: { row: InventorySbomRow }) {
  const nodes = [
    sbomOwnerBoundary(row),
    fileName(row.manifestPath),
    ...row.dependencyPath,
    `${row.name}@${row.version || "unresolved"}`,
  ].filter((value, index, values) => values.indexOf(value) === index);
  return (
    <section className="sbom-route" aria-labelledby="sbom-route-heading">
      <span>Recorded dependency route</span>
      <h3 id="sbom-route-heading">How this component enters the repository</h3>
      <p>
        This is a manifest relationship from the sealed snapshot, not proof that production loaded
        or executed the package.
      </p>
      <ol>
        {nodes.map((node, index) => (
          <li key={`${node}-${index}`}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{node}</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

function SbomVersionCohort({
  row,
  sbomRows,
}: {
  row: InventorySbomRow;
  sbomRows: readonly InventorySbomRow[];
}) {
  const cohort = sbomRows.filter(
    (peer) => peer.name === row.name && peer.ecosystem === row.ecosystem,
  );
  const versions = new Set(cohort.map((peer) => peer.version || "unresolved"));
  return (
    <section className="sbom-cohort" aria-labelledby="sbom-cohort-heading">
      <div>
        <span>Repository blast radius</span>
        <h3 id="sbom-cohort-heading">{row.name} version cohort</h3>
        <p>
          {cohort.length} declaration{cohort.length === 1 ? "" : "s"} across {versions.size} exact
          version{versions.size === 1 ? "" : "s"}. This identifies the lockfiles that need one
          coordinated remediation decision.
        </p>
      </div>
      <div className="sbom-cohort__table" role="table" aria-label={`${row.name} declarations`}>
        <div role="row" className="sbom-cohort__head">
          <span role="columnheader">Version</span>
          <span role="columnheader">Dependency route</span>
          <span role="columnheader">Manifest</span>
        </div>
        {cohort.slice(0, 8).map((peer) => (
          <div role="row" key={peer.id}>
            <code role="cell">{peer.version || "unresolved"}</code>
            <span role="cell">{peer.dependencyPath.join(" → ") || "No path recorded"}</span>
            <code role="cell">{peer.manifestPath}</code>
          </div>
        ))}
      </div>
    </section>
  );
}

function cbomStamp(row: InventoryCbomRow): string {
  if (row.algorithmClass === "quantum_vulnerable_public_key") return "quantum-vulnerable";
  if (row.algorithmClass === "hybrid") return "hybrid";
  if (row.algorithmClass === "post_quantum_standardized") return "standardized PQC";
  return "inventory";
}

function hybridReadinessCopy(row: InventoryCbomRow): string {
  if (row.algorithmClass === "hybrid") return "Classical and standardized PQC APIs coexist";
  if (row.algorithmClass === "post_quantum_standardized") return "Standardized PQC API observed";
  if (row.algorithmClass === "quantum_vulnerable_public_key") return "Classical public-key only";
  return "Not a public-key migration item";
}

function SbomFindingsDefault({ row }: { row: InventorySbomRow }) {
  return (
    <div className="inventory-briefing__slot">
      <p>Affected findings load in the findings slot.</p>
      <p>
        Related SCA findings open from this component. No linked finding is not a clean advisory
        state.
      </p>
      <a href={componentFindingsHref(row)}>Open related findings</a>
    </div>
  );
}

function SbomForensics({
  row,
  sbomRows,
}: {
  row: InventorySbomRow;
  sbomRows: readonly InventorySbomRow[];
}) {
  const declaredAlongside = sbomRows.filter(
    (peer) => peer.manifestPath === row.manifestPath && peer.id !== row.id,
  );
  return (
    <>
      <details className="inventory-briefing__forensics" open>
        <summary>Dependency evidence and relationships</summary>
        <div className="inventory-briefing__grid">
          <div>
            <span>Version</span>
            <strong>{row.resolved ? row.version : "Unresolved"}</strong>
          </div>
          <div>
            <span>Ecosystem</span>
            <strong>{row.ecosystem}</strong>
          </div>
          <div>
            <span>License</span>
            <strong>{row.license ?? "Not declared"}</strong>
          </div>
          <div>
            <span>Dependency type</span>
            <strong>{row.local ? "Local component" : "Declared dependency"}</strong>
          </div>
          <div className="inventory-briefing__wide">
            <span>Package identity</span>
            <code>{row.purl}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Declared in</span>
            <code>{row.manifestPath}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Additional declarations</span>
            <p>{row.extras.length ? row.extras.join(" · ") : "None recorded"}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Dependency path</span>
            <p>{sbomDependencyPathCopy(row)}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Upgrade option</span>
            <p>{SBOM_UPGRADE_OPTION_COPY}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Reachability</span>
            <p>
              Unknown from static source. A declared manifest component is not proof that the
              runtime loaded it.
            </p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Advisories</span>
            <p>
              Related SCA findings open from this component. No linked finding is not a clean
              advisory state.
            </p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Declared alongside in this manifest</span>
            {declaredAlongside.length ? (
              <ul className="inventory-briefing__peers">
                {declaredAlongside.slice(0, 8).map((peer) => (
                  <li key={peer.id}>
                    <code>
                      {peer.name}@{peer.version || "unresolved"}
                    </code>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No other components were parsed from this manifest.</p>
            )}
          </div>
        </div>
      </details>
      <details
        className="inventory-briefing__forensics inventory-briefing__forensics--custody"
        open
      >
        <summary>Parser, custody, and immutable hash provenance</summary>
        <div className="inventory-briefing__grid">
          <div>
            <span>Parser</span>
            <strong>{row.parserVersion}</strong>
          </div>
          <div>
            <span>Replacement origin</span>
            <strong>{row.replacedFrom ?? "None"}</strong>
          </div>
          <div>
            <span>Scan identity</span>
            <code>{row.scanId}</code>
          </div>
          <div>
            <span>Snapshot identity</span>
            <code>{row.snapshotId}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Immutable manifest SHA-256</span>
            <code>{row.manifestSha256}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Receipted at</span>
            <strong>{row.createdAt}</strong>
          </div>
        </div>
      </details>
    </>
  );
}

function CbomForensics({
  row,
  cbomRows,
}: {
  row: InventoryCbomRow;
  cbomRows: readonly InventoryCbomRow[];
}) {
  const sameFile = cbomRows.filter(
    (peer) => peer.location.path === row.location.path && peer.id !== row.id,
  );
  const migrationCohort = cbomRows.filter(
    (peer) =>
      peer.id !== row.id &&
      peer.algorithm !== null &&
      peer.algorithm === row.algorithm &&
      (peer.algorithmClass === "quantum_vulnerable_public_key" ||
        peer.algorithmClass === "hybrid" ||
        row.algorithmClass === "quantum_vulnerable_public_key" ||
        row.algorithmClass === "hybrid"),
  );
  return (
    <>
      <details className="inventory-briefing__forensics">
        <summary>Open full migration evidence and decision context</summary>
        <div className="inventory-briefing__grid">
          <div>
            <span>Classification</span>
            <strong>{row.algorithmClass.replaceAll("_", " ")}</strong>
          </div>
          <div>
            <span>Hybrid readiness</span>
            <strong>{hybridReadinessCopy(row)}</strong>
          </div>
          <div>
            <span>Confidence</span>
            <strong>{row.confidenceBand}</strong>
          </div>
          <div>
            <span>Use</span>
            <strong>{row.useContext.replaceAll("_", " ")}</strong>
          </div>
          <div className="inventory-briefing__wide">
            <span>Protocol role</span>
            <strong>{cbomProtocolRoleCopy(row)}</strong>
          </div>
          <div className="inventory-briefing__wide">
            <span>Certificate identity</span>
            <p>{CBOM_CERTIFICATE_IDENTITY_COPY}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Key storage</span>
            <p>{CBOM_KEY_STORAGE_COPY}</p>
          </div>
          <div>
            <span>Resolution</span>
            <strong>{row.algorithmResolution.replaceAll("_", " ")}</strong>
          </div>
          <div>
            <span>Observed API</span>
            <code>{row.api}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Migration dependency</span>
            <p>{row.migrationDependency}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Functional impact</span>
            <p>{row.functionalImpact}</p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Evidence location</span>
            <a href={row.sourceHref}>
              {row.location.path}:{row.location.lineStart}
            </a>
          </div>
          <div>
            <span>Finding linkage</span>
            <strong>
              {row.findingRuleId ? displayRuleId(row.findingRuleId) : "Inventory observation only"}
            </strong>
          </div>
          <div>
            <span>MOSCA urgency</span>
            <strong>
              {row.moscaUrgency?.status.replaceAll("_", " ") ?? "Business inputs required"}
            </strong>
          </div>
          <div className="inventory-briefing__wide">
            <span>Retention horizon</span>
            <p>
              Unknown from static source. Key and ciphertext lifetime need runtime or operator
              evidence before a migration deadline is claimed.
            </p>
          </div>
          <div className="inventory-briefing__wide">
            <span>Other crypto uses in this file</span>
            {sameFile.length ? (
              <ul className="inventory-briefing__peers">
                {sameFile.slice(0, 8).map((peer) => (
                  <li key={peer.id}>
                    <code>
                      {peer.api} · {peer.algorithm ?? "dynamic"} · line {peer.location.lineStart}
                    </code>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No other cryptographic uses were inventoried in this file.</p>
            )}
          </div>
          <div className="inventory-briefing__wide">
            <span>Migration cohort (same algorithm)</span>
            {migrationCohort.length ? (
              <ul className="inventory-briefing__peers">
                {migrationCohort.slice(0, 8).map((peer) => (
                  <li key={peer.id}>
                    <code>
                      {peer.location.path}:{peer.location.lineStart} · {peer.api}
                    </code>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No other inventoried uses share this algorithm.</p>
            )}
          </div>
        </div>
      </details>
      <details className="inventory-briefing__forensics inventory-briefing__forensics--custody">
        <summary>Show catalog, source binding, and immutable custody hashes</summary>
        <div className="inventory-briefing__grid">
          <div>
            <span>Resolution</span>
            <strong>{row.algorithmResolution.replaceAll("_", " ")}</strong>
          </div>
          <div>
            <span>Catalog</span>
            <code>{row.catalogVersion}</code>
          </div>
          <div>
            <span>Scan identity</span>
            <code>{row.scanId}</code>
          </div>
          <div>
            <span>Snapshot identity</span>
            <code>{row.snapshotId}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>Source binding</span>
            <code>{row.bindingProvenance}</code>
          </div>
          <div className="inventory-briefing__wide">
            <span>File SHA-256</span>
            <code>{row.fileSha256}</code>
          </div>
        </div>
      </details>
    </>
  );
}

export function InventoryBriefing(props: {
  selection: InventorySelection | null;
  sbomRows: readonly InventorySbomRow[];
  cbomRows: readonly InventoryCbomRow[];
  onClose: () => void;
  findingsSlot?: ReactNode;
  cbomBodySlot?: ((activeTab: InventoryDetailTab) => ReactNode) | null;
}): JSX.Element | null {
  const { selection, sbomRows, cbomRows, onClose, findingsSlot, cbomBodySlot } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [activeTab, setActiveTab] = useState<InventoryDetailTab>("summary");
  useEffect(() => {
    const dialog = dialogRef.current;
    if (selection && dialog && !dialog.open) dialog.showModal();
    if (!selection && dialog?.open) dialog.close();
  }, [selection]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: each newly selected issue starts on Summary
  useEffect(() => setActiveTab("summary"), [selection?.kind, selection?.row.id]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!selection) return null;

  const close = () => {
    dialogRef.current?.close();
    onClose();
  };
  const kicker = selection.kind === "sbom" ? "SOFTWARE COMPONENT" : "CRYPTOGRAPHIC USE";
  const stamp = selection.kind === "sbom" ? sbomStamp(selection.row) : cbomStamp(selection.row);

  return (
    <dialog
      className={`inventory-briefing inventory-briefing--${selection.kind}`}
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby="inventory-brief-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <header>
        <div className="inventory-briefing__kicker-row">
          <p className="inventory-briefing__tab">{kicker}</p>
          <span className="inventory-briefing__stamp" data-stamp={stamp}>
            {stamp}
          </span>
        </div>
        <button
          className="inventory-briefing__x"
          type="button"
          aria-label="Close inventory briefing"
          onClick={close}
        >
          <X aria-hidden="true" size={16} />
        </button>
      </header>
      <InventoryTabs activeTab={activeTab} onChange={setActiveTab} />
      {selection.kind === "sbom" ? (
        <SbomBody
          row={selection.row}
          sbomRows={sbomRows}
          findingsSlot={findingsSlot}
          activeTab={activeTab}
          onClose={close}
        />
      ) : (
        <CbomBody
          row={selection.row}
          cbomRows={cbomRows}
          {...(cbomBodySlot === undefined ? {} : { cbomBodySlot })}
          activeTab={activeTab}
          onClose={close}
        />
      )}
    </dialog>
  );
}

function SbomBody({
  row,
  sbomRows,
  findingsSlot,
  activeTab,
  onClose,
}: {
  row: InventorySbomRow;
  sbomRows: readonly InventorySbomRow[];
  findingsSlot?: ReactNode;
  activeTab: InventoryDetailTab;
  onClose: () => void;
}) {
  const title = `${row.name}${row.version ? `@${row.version}` : ""}`;
  const findingsHref = componentFindingsHref(row);
  return (
    <>
      <h2 id="inventory-brief-title">{title}</h2>
      <div className="inventory-briefing__pins">
        <span className="inventory-briefing__file">{fileName(row.manifestPath)}</span>
        <span className="inventory-briefing__chip">{row.ecosystem}</span>
        <span className="inventory-briefing__chip">{row.license ?? "Not declared"}</span>
        <span className="inventory-briefing__mono">{row.purl}</span>
      </div>
      <div className="inventory-briefing__body">
        <InventoryPanel tab="summary" activeTab={activeTab}>
          <div className="inventory-briefing__metrics">
            <article className="inventory-briefing__metric">
              <span>What you have</span>
              <strong>{title}</strong>
              <p>
                {row.local
                  ? "Local source component declared in the scanned repository."
                  : "Third-party dependency declared by an immutable manifest."}
              </p>
            </article>
            <article className="inventory-briefing__metric">
              <span>Why it matters</span>
              <strong>
                {row.resolved ? "Manifest-pinned dependency" : "Unresolved dependency"}
              </strong>
              <p>
                {row.resolved
                  ? `Declared in ${row.manifestPath}; open related SCA/container findings before deciding exposure.`
                  : "Resolve the version before treating advisory coverage as complete."}
              </p>
            </article>
            <article className="inventory-briefing__metric">
              <span>Next action</span>
              <strong>Verify patched version</strong>
              <p>
                Check whether {title} has linked findings, a safer replacement, or a manual
                version-review decision.
              </p>
            </article>
          </div>
          <div className="inventory-briefing__callout is-takeaway">
            <span>Key takeaway</span>
            <p>{sbomDependencyPathCopy(row)}</p>
          </div>
          <div className="inventory-briefing__grid sbom-summary-ledger">
            <div>
              <span>Dependency role</span>
              <strong>{sbomDependencyRole(row)}</strong>
            </div>
            <div>
              <span>Owning code boundary</span>
              <strong>{sbomOwnerBoundary(row)}</strong>
            </div>
            <div>
              <span>Evidence confidence</span>
              <strong>{row.resolved ? "Exact version observed" : "Version unresolved"}</strong>
            </div>
            <div>
              <span>Runtime conclusion</span>
              <strong>Not established by a lockfile</strong>
            </div>
            <div className="inventory-briefing__wide">
              <span>Surgical next step</span>
              <p>{sbomSurgicalAction(row)}</p>
            </div>
          </div>
        </InventoryPanel>
        <InventoryPanel tab="evidence" activeTab={activeTab}>
          <SbomDependencyRoute row={row} />
          <SbomForensics row={row} sbomRows={sbomRows} />
        </InventoryPanel>
        <InventoryPanel tab="investigate" activeTab={activeTab}>
          <SbomDependencyRoute row={row} />
          <SbomVersionCohort row={row} sbomRows={sbomRows} />
          {findingsSlot ?? <SbomFindingsDefault row={row} />}
        </InventoryPanel>
        <InventoryPanel tab="review" activeTab={activeTab}>
          <div className="inventory-briefing__callout is-implications">
            <span>Review decision</span>
            <p>{sbomSurgicalAction(row)}</p>
            <p>
              Do not close this component as clean merely because no finding is linked. Record
              advisory coverage, confirm runtime reachability, and retain the replacement lockfile
              hash as exit evidence.
            </p>
          </div>
          <div className="inventory-briefing__grid sbom-review-gates">
            <div>
              <span>1 · Establish exposure</span>
              <p>
                Confirm whether {row.name} is imported, bundled, or present in the deployed image.
              </p>
            </div>
            <div>
              <span>2 · Change the right owner</span>
              <p>{sbomSurgicalAction(row)}</p>
            </div>
            <div>
              <span>3 · Prove removal</span>
              <p>Rerun the scan and require the old package URL to be absent from the SBOM.</p>
            </div>
            <div>
              <span>4 · Prevent regression</span>
              <p>
                Pin the accepted version policy and fail delivery if this exact identity returns.
              </p>
            </div>
          </div>
        </InventoryPanel>
      </div>
      <footer>
        <button type="button" className="inventory-briefing__ghost" onClick={onClose}>
          Close
        </button>
        <a className="inventory-briefing__primary" href={findingsHref}>
          Open related findings
        </a>
      </footer>
    </>
  );
}

function CbomBody({
  row,
  cbomRows,
  cbomBodySlot,
  activeTab,
  onClose,
}: {
  row: InventoryCbomRow;
  cbomRows: readonly InventoryCbomRow[];
  cbomBodySlot?: ((activeTab: InventoryDetailTab) => ReactNode) | null;
  activeTab: InventoryDetailTab;
  onClose: () => void;
}) {
  const title = cryptoDisplayName(row);
  const relatedHref = cryptoFindingsHref(row);
  const isPqcPriority =
    row.algorithmClass === "quantum_vulnerable_public_key" || row.algorithmClass === "hybrid";
  const cryptoAction = isPqcPriority
    ? "Prioritize migration review"
    : row.algorithmClass === "post_quantum_standardized"
      ? "Preserve as readiness evidence"
      : "Keep inventoried, not escalated";
  const evidenceLabel = `${row.location.path}:${row.location.lineStart}`;
  return (
    <>
      <h2 id="inventory-brief-title">{title}</h2>
      <div className="inventory-briefing__pins">
        <a
          className="inventory-briefing__file"
          href={row.sourceHref}
          target="_blank"
          rel="noreferrer"
        >
          {evidenceLabel}
        </a>
      </div>
      <div className="inventory-briefing__body">
        <InventoryPanel tab="summary" activeTab={activeTab}>
          <div className="inventory-briefing__metrics">
            <article className="inventory-briefing__metric">
              <span>Crypto observed</span>
              <strong>{title}</strong>
              <p>
                {row.useContext.replaceAll("_", " ")} at {evidenceLabel}
              </p>
            </article>
            <article className="inventory-briefing__metric">
              <span>Why it matters</span>
              <strong>{row.algorithmClass.replaceAll("_", " ")}</strong>
              <p>{cbomProtocolRoleCopy(row)}</p>
            </article>
            <article className="inventory-briefing__metric">
              <span>Next action</span>
              <strong>{cryptoAction}</strong>
              <p>
                {isPqcPriority
                  ? "Confirm key lifetime and protocol role, then plan a hybrid or standardized PQC migration path."
                  : "Keep this as inventory evidence; do not inflate it into a PQC finding without a vulnerable public-key role."}
              </p>
            </article>
          </div>
        </InventoryPanel>
        {cbomBodySlot ? (
          cbomBodySlot(activeTab)
        ) : (
          <InventoryPanel tab={activeTab} activeTab={activeTab}>
            <CbomForensics row={row} cbomRows={cbomRows} />
          </InventoryPanel>
        )}
      </div>
      <footer>
        <button type="button" className="inventory-briefing__ghost" onClick={onClose}>
          Close
        </button>
        {relatedHref ? (
          <a className="inventory-briefing__primary" href={relatedHref}>
            Open related findings
          </a>
        ) : (
          <span className="inventory-briefing__muted">
            No vulnerability finding is linked to this inventory observation.
          </span>
        )}
        <a
          className="inventory-briefing__ghost"
          href={row.sourceHref}
          target="_blank"
          rel="noreferrer"
        >
          Open exact source evidence
        </a>
      </footer>
    </>
  );
}
